import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/store/useStore";

/**
 * Audio alert for new QR orders — uses Web Audio API (no external file needed).
 * Two-tone chime that repeats 3 times for kitchen attention.
 */
const playOrderChime = (repeat = 1) => {
  const playOnce = (delay = 0) => {
    setTimeout(() => {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();

        // First tone
        const osc1 = ctx.createOscillator();
        const gain1 = ctx.createGain();
        osc1.connect(gain1);
        gain1.connect(ctx.destination);
        osc1.frequency.value = 880;
        osc1.type = "sine";
        gain1.gain.value = 0.7;
        osc1.start();
        osc1.stop(ctx.currentTime + 0.2);

        // Second tone (higher)
        setTimeout(() => {
          try {
            const ctx2 = new (window.AudioContext || window.webkitAudioContext)();
            const osc2 = ctx2.createOscillator();
            const gain2 = ctx2.createGain();
            osc2.connect(gain2);
            gain2.connect(ctx2.destination);
            osc2.frequency.value = 1100;
            osc2.type = "sine";
            gain2.gain.value = 0.7;
            osc2.start();
            osc2.stop(ctx2.currentTime + 0.25);
          } catch { /* ignore */ }
        }, 180);
      } catch {
        // Audio not available
      }
    }, delay);
  };

  for (let i = 0; i < repeat; i++) {
    playOnce(i * 500);
  }
};

/**
 * Hook: polls for new QR menu orders and fires toast notifications.
 *
 * @param {Object} opts
 * @param {boolean} opts.enabled    – whether polling is active
 * @param {number}  opts.interval   – poll interval in ms (default 10 000)
 * @param {boolean} opts.sound      – play audio chime (default true)
 * @returns {{ pendingOrders, pendingCount, acknowledge }}
 */
export function useQROrderNotifications({ enabled = true, interval = 10000, sound = true, alertRepeat = 1 } = {}) {
  const [pendingOrders, setPendingOrders] = useState([]);
  const seenIds = useRef(new Set());
  const isFirstPoll = useRef(true);

  const fetchPending = useCallback(async () => {
    if (!enabled) return;
    try {
      const data = await api.get("/qr-orders/pending");
      const orders = data.orders || [];
      setPendingOrders(orders);

      // Only notify for truly new orders (not on first load)
      if (!isFirstPoll.current) {
        const newOrders = orders.filter((o) => !seenIds.current.has(o.id));
        if (newOrders.length > 0) {
          if (sound) playOrderChime(alertRepeat);
          newOrders.forEach((o) => {
            const itemCount = (o.items || []).reduce((s, i) => s + (i.quantity || 1), 0);
            const type = o.order_type === "dine_in"
              ? `Table ${o.table_number || "?"}`
              : o.order_type === "delivery" ? "Delivery" : "Takeaway";
            toast.info(
              `New QR Order ${o.order_number} — ${itemCount} item${itemCount !== 1 ? "s" : ""}, ${type}`,
              { duration: 8000 }
            );
          });
        }
      }

      // Track all seen IDs
      orders.forEach((o) => seenIds.current.add(o.id));
      isFirstPoll.current = false;
    } catch {
      // Silently ignore polling errors
    }
  }, [enabled, sound]);

  useEffect(() => {
    if (!enabled) return;
    fetchPending();
    const timer = setInterval(fetchPending, interval);
    return () => clearInterval(timer);
  }, [enabled, interval, fetchPending]);

  const acknowledge = useCallback(async (orderIds) => {
    if (!orderIds?.length) return;
    try {
      await api.post("/qr-orders/acknowledge", orderIds);
      setPendingOrders((prev) => prev.filter((o) => !orderIds.includes(o.id)));
    } catch {
      // Ignore
    }
  }, []);

  return {
    pendingOrders,
    pendingCount: pendingOrders.length,
    acknowledge,
    refresh: fetchPending,
  };
}
